import { Component, OnInit, Inject } from '@angular/core';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { filter, mergeMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { InteractionMode } from '../../../core/types/interaction-mode';
import { ClaimService } from '../../../core/services/claims.service';
import { NotificationService } from '../../../core/services/notification.service';
import { DialogProviderService } from '../../../core/services/dialog-provider.service';
import {
  ClaimOption,
  ClaimOptionCode,
  ClaimUser,
  DeserializedClaim,
} from '../../../core/types/claim';

@Component({
    selector: 'itfg-claim-edit',
    templateUrl: './claim-edit.component.html',
    styleUrls: ['./claim-edit.component.scss'],
    standalone: false
})
export class ClaimEditComponent implements OnInit {
  public modes: typeof InteractionMode = InteractionMode;
  public mode: InteractionMode;
  public editMode: boolean;
  public claimForm: UntypedFormGroup;
  public userId: number;
  public claimOptions: ClaimOption[] = [];
  public forgetClaim: boolean;
  public pendingApproval: boolean;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    private data,
    private dialogRef: MatDialogRef<ClaimEditComponent>,
    public formBuilder: UntypedFormBuilder,
    // Services
    private claimService: ClaimService,
    private notificationService: NotificationService,
    private dialogService: DialogProviderService,
    private translateService: TranslateService
  ) {
    this.mode = data.mode;
    this.editMode = this.mode === this.modes.EDIT;
    this.userId = data.userId;
    this.forgetClaim =
      this.editMode &&
      data.claim.claimOption?.code === ClaimOptionCode.FORGET_CLIENT;
    this.pendingApproval =
      this.forgetClaim && !data.claim.completed && !data.claim.accepted;
    this.claimForm = this.createClaimForm();
  }

  ngOnInit() {
    if (!this.editMode) {
      this.claimService
        .getClaimOptions$()
        .subscribe((claimOptions: ClaimOption[]) => (this.claimOptions = claimOptions));
    }
  }

  addNewClaim(event) {
    if (this.claimForm.valid) {
      const claimToSave = this.parseFormDataToClaimBody(
        this.userId,
        this.claimForm
      );

      if (!this.editMode) {
        // Add new claim
        this.claimService
          .saveClaim$(claimToSave)
          .subscribe(res => this.dialogRef.close(true));
      } else if (this.pendingApproval && this.claimForm.get('accepted').value) {
        this.saveAndAcceptClaim(claimToSave);
      } else {
        // Edit existing claim
        this.claimService
          .updateClaim$(this.data.claim.id, claimToSave)
          .subscribe(res => this.dialogRef.close(true));
      }
    } else {
      this.showInvalidFormNotification();
    }
  }

  showInvalidFormNotification() {
    this.notificationService.showLocalizedErrorMessage({
      notificationText: 'forms.formSavedIsInvalid',
    });
  }

  parseFormDataToClaimBody(userId: number, form: UntypedFormGroup): DeserializedClaim {
    if (this.editMode) {
      return {
        user: {
          id: this.userId,
        },
        ...this.claimForm.getRawValue(),
      };
    }

    const claimOption: ClaimOption = this.claimForm.value.claimOption;
    return {
      user: { id: this.userId } as ClaimUser,
      claimOption: { id: claimOption.id } as ClaimOption,
      subject: claimOption.name,
    };
  }

  createClaimForm(): UntypedFormGroup {
    const fb = this.formBuilder;

    if (!this.editMode) {
      return fb.group({
        claimOption: [null, Validators.required],
      });
    }

    return fb.group({
      notes: [this.data.claim.notes],
      completed: [
        {
          value: this.data.claim.completed,
          disabled: this.forgetClaim && !this.data.claim.accepted,
        },
      ],
      accepted: [
        { value: this.data.claim.accepted, disabled: !this.pendingApproval },
      ],
    });
  }

  saveAndAcceptClaim(claimToSave: DeserializedClaim) {
    this.dialogService
      .openConfirm({
        title: this.translateService.instant('claims.accept'),
        message: this.translateService.instant('claims.acceptConfirm', {
          claimId: this.data.claim.id,
        }),
        disableClose: false,
        cancelButton: this.translateService.instant('global.cancel'),
        acceptButton: this.translateService.instant('global.confirm'),
      })
      .afterClosed()
      .pipe(
        filter((accept: boolean) => accept === true),
        mergeMap(() =>
          this.claimService.updateClaim$(this.data.claim.id, claimToSave)
        ),
        mergeMap(() => this.claimService.acceptClaim$(this.data.claim.id))
      )
      .subscribe({
        next: () => {
          this.notificationService.showLocalizedSuccessMessage({
            notificationText: 'claims.acceptSuccess',
          });
          this.dialogRef.close(true);
        },
        error: (err: HttpErrorResponse) => {
          if (err.status === HttpStatusCode.ExpectationFailed) {
            this.notificationService.showLocalizedErrorMessage({
              notificationText: 'claims.acceptNotAllowed',
            });
            this.dialogRef.close(true);
          }
        },
      });
  }

  onCloseClicked() {
    this.dialogRef.close();
  }
}
